import { Injectable } from '@angular/core';
import { AzureBlobStorageService } from '@app/shared/services/api/azure-blob-storage.service';
import { InformationService } from '@app/shared/services/api/information.service';
import { AuthService } from '@app/shared/services/auth.service';
import { isNil, orderBy } from 'lodash';
import * as moment from 'moment';
import { Observable, of } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';
import { BlobFileDto } from '@app/socket-service-page/models/blob-files';
import { InformationUtlity } from '../information-utility';
import { IContentDialogComponent } from '../models/content-dialog';
import { Information, InformationType, SaveInformationRequest } from '../models/information';

type SortableInformation = Information & {
  priority: number;
  sortColumn: Date;
};

@Injectable({
  providedIn: 'root'
})
export class InformationDataService {
  informationTypes: InformationType[] = [];
  highlightId: string | null = null;

  constructor(
    private readonly auth: AuthService,
    private readonly azureBlobStorage: AzureBlobStorageService,
    private readonly information: InformationService
  ) {
    this.information.getTypes().subscribe((infoTypes: InformationType[]) => {
      this.informationTypes = infoTypes;
    });
  }

  public mapContentInformation(content: IContentDialogComponent): SaveInformationRequest {
    const validFrom = this.getValidDateIsoString(content.eventDate, content.eventStart);
    const validTo = this.getValidDateIsoString(content.eventDate, content.eventEnd);

    return {
      id: content.id,
      title: content.title || '',
      description: content.description || '',
      typeId: this.resolveTypeId(content),
      image: content.image ?? null,
      validFrom,
      validTo,
      createdBy: `${this.auth.currentUser.firstname} ${this.auth.currentUser.lastname}`,
      modifiedBy: `${this.auth.currentUser.firstname} ${this.auth.currentUser.lastname}`,
      setTimeFrom: !isNil(content.eventStart) && content.eventStart.trim().length > 0,
      setTimeTo: !isNil(content.eventEnd) && content.eventEnd.trim().length > 0
    };
  }

  public save(content: IContentDialogComponent): Observable<Information> {
    const request = this.mapContentInformation(content);

    if (content.id) {
      return this.information
        .putInformation(request)
        .pipe(
          mergeMap((savedInformation: Information) =>
            content.thumbnail
              ? this.azureBlobStorage
                  .uploadBlob(`information/${savedInformation.id}/${savedInformation.image}`, content.thumbnail, content.thumbnail.name)
                  .pipe(map(() => savedInformation))
              : of(savedInformation)
          )
        );
    }

    return this.information
      .postInformation(request)
      .pipe(
        mergeMap((savedInformation: Information) =>
          content.thumbnail
            ? this.azureBlobStorage
                .uploadBlob(`information/${savedInformation.id}/${savedInformation.image}`, content.thumbnail, content.thumbnail.name)
                .pipe(map(() => savedInformation))
            : of(savedInformation)
        )
      );
  }

  public isEvent(information: Information): boolean {
    return this.getInformationTypeById(information.typeId)?.code.toLowerCase() === 'event';
  }

  public getFiles(information: IContentDialogComponent): Observable<BlobFileDto[]> {
    if (!information.id) {
      return of([]);
    }

    return this.azureBlobStorage.getFiles(`information/${information.id}/attachments`);
  }

  public uploadAttachment(informationId: string, file: File): Observable<object> {
    return this.azureBlobStorage.uploadBlob(`information/${informationId}/attachments/${file.name}`, file, file.name);
  }

  public deleteAttachments(informationId: string, fileName: string): Observable<object> {
    return this.azureBlobStorage.deleteFile(`information/${informationId}/attachments/${fileName}`);
  }

  public deleteThumbnail(informationId: string, fileName: string): Observable<object> {
    return this.azureBlobStorage.deleteFile(`information/${informationId}/${fileName}`);
  }

  public filterActiveRecords(data: Information[]): Information[] {
    const dateNow = new Date();

    return data.filter((information: Information) => {
      if (information.typeId === 2 && information.validTo) {
        const parsedDate = new Date(information.validTo);
        const eventDate = new Date(
          Date.UTC(parsedDate.getFullYear(), parsedDate.getMonth(), parsedDate.getDate(), parsedDate.getHours(), parsedDate.getMinutes(), 0, 0)
        );

        if (isNil(information.setTimeTo) || information.setTimeTo === false) {
          eventDate.setDate(eventDate.getDate() + 1);
          eventDate.setHours(0, 0, 0, 0);
        }

        return eventDate >= dateNow;
      }

      return true;
    });
  }

  public sortRecords(records: Information[]): Information[] {
    const newList: SortableInformation[] = records.map((data: Information): SortableInformation => {
      const sortableData: SortableInformation = {
        ...data,
        priority: 3,
        sortColumn: this.getStartOfDay(data.modifiedOn)
      };

      if (data.typeId === 2 && data.validFrom && data.setTimeFrom) {
        sortableData.priority = 1;
        const parsedDate = new Date(data.validFrom);

        sortableData.sortColumn = new Date(
          Date.UTC(parsedDate.getFullYear(), parsedDate.getMonth(), parsedDate.getDate(), parsedDate.getHours(), parsedDate.getMinutes(), 0, 0)
        );
      } else if (data.typeId === 2 && isNil(data.validFrom)) {
        sortableData.priority = 2;
        sortableData.sortColumn = this.getStartOfDay(data.modifiedOn);
      }

      return sortableData;
    });

    const events = orderBy(
      newList.filter((data: SortableInformation) => data.typeId === 2),
      ['priority', 'sortColumn'],
      ['asc', 'asc']
    );

    const others = orderBy(
      newList.filter((data: SortableInformation) => data.typeId !== 2),
      ['modifiedOn'],
      ['desc']
    );

    return events.concat(others);
  }

  private resolveTypeId(content: IContentDialogComponent): number {
    if (InformationUtlity.isCustomerComplaintInformation(content)) {
      return this.getInformationTypeId('complaints');
    }

    return content.isEvent ? this.getInformationTypeId('event') : this.getInformationTypeId('document');
  }

  private getInformationTypeId(code: string): number {
    const informationType = this.informationTypes.find((type: InformationType) => type.code.toLowerCase() === code.toLowerCase());

    if (!informationType) {
      throw new Error(`Information type "${code}" was not found.`);
    }

    return informationType.id;
  }

  private getInformationTypeById(typeId: number): InformationType | undefined {
    return this.informationTypes.find((type: InformationType) => type.id === typeId);
  }

  private getValidDateIsoString(eventDate: Date | null, eventTime: string | null): string | null {
    if (!eventDate) {
      return null;
    }

    const parsedDate = moment.utc(
      moment(`${moment(eventDate).year()}-${moment(eventDate).month() + 1}-${moment(eventDate).date()} ${eventTime || ''}`, 'YYYY-MM-DD hh:mm:ss a')
    );

    return parsedDate.toDate().toISOString();
  }

  private getStartOfDay(date: string): Date {
    const modifiedDate = new Date(date);
    modifiedDate.setHours(0);
    modifiedDate.setMinutes(0);
    modifiedDate.setSeconds(0);
    modifiedDate.setMilliseconds(0);

    return modifiedDate;
  }
}
